/**
 * Every upload rule, and every message a refusal produces.
 *
 * Imported by both sides: `upload-limits.ts` on the server wraps a returned reason in a Payload
 * `ValidationError`, and `validate-in-browser.ts` wraps the same reason in an `Error`. Keep this file
 * free of `payload`, `jsdom` and anything else Node-only, or the admin bundle breaks — that constraint
 * is why the rules live here rather than in the hooks that use them.
 */
import {
  AUDIO_MAX_FILE_SIZE_MB,
  BYTES_PER_MB,
  DOCUMENT_MAX_FILE_SIZE_MB,
  FONT_MAX_FILE_SIZE_MB,
  IMAGE_MAX_DIMENSION_PX,
  IMAGE_MAX_FILE_SIZE_MB,
  SVG_MAX_FILE_SIZE_MB,
  VIDEO_MAX_FILE_SIZE_MB,
} from '@/constants/uploads';
import { bytesToMB } from '@/utils/file';

export const SVG_MIME_TYPE = 'image/svg+xml';

const SVG_ROOT_ELEMENT = /<svg[\s>]/i;

/** How much of a file has to be read to decide whether it is an SVG. */
export const SVG_SNIFF_BYTES = 4096;

/**
 * Whether these bytes are an SVG, given what a container sniff made of them and the text they start
 * with. An SVG carries no header, so only a file nothing claims — or one claimed as XML — is a
 * candidate, and the root element settles it. Both the browser and the server decide it here.
 */
export const isSvgContent = (detectedMimeType: string | undefined, head: string): boolean =>
  (!detectedMimeType || detectedMimeType === 'application/xml') && SVG_ROOT_ELEMENT.test(head);

/**
 * The one table every upload rule reads: what we accept, which class it belongs to for the size
 * limits, and the formats Cloudinary must be told to accept. Adding a type here is the whole change.
 */
const MEDIA_TYPES: Record<string, { formats: string[]; mediaClass: MediaClass }> = {
  'application/font-woff': { formats: ['woff'], mediaClass: 'font' },
  'application/font-woff2': { formats: ['woff2'], mediaClass: 'font' },
  'application/pdf': { formats: ['pdf'], mediaClass: 'document' },
  'application/vnd.ms-fontobject': { formats: ['eot'], mediaClass: 'font' },
  'application/x-font-opentype': { formats: ['otf'], mediaClass: 'font' },
  'application/x-font-ttf': { formats: ['ttf'], mediaClass: 'font' },
  'audio/mp3': { formats: ['mp3'], mediaClass: 'audio' },
  'audio/mpeg': { formats: ['mp3'], mediaClass: 'audio' },
  'font/otf': { formats: ['otf'], mediaClass: 'font' },
  'font/ttf': { formats: ['ttf'], mediaClass: 'font' },
  'font/woff': { formats: ['woff'], mediaClass: 'font' },
  'font/woff2': { formats: ['woff2'], mediaClass: 'font' },
  'image/avif': { formats: ['avif'], mediaClass: 'image' },
  'image/gif': { formats: ['gif'], mediaClass: 'image' },
  'image/jpeg': { formats: ['jpg', 'jpeg'], mediaClass: 'image' },
  'image/png': { formats: ['png'], mediaClass: 'image' },
  'image/vnd.microsoft.icon': { formats: ['ico'], mediaClass: 'image' },
  'image/webp': { formats: ['webp'], mediaClass: 'image' },
  'image/x-icon': { formats: ['ico'], mediaClass: 'image' },
  [SVG_MIME_TYPE]: { formats: ['svg'], mediaClass: 'image' },
  'video/mp4': { formats: ['mp4'], mediaClass: 'video' },
  'video/quicktime': { formats: ['mov'], mediaClass: 'video' },
  'video/webm': { formats: ['webm'], mediaClass: 'video' },
};

const EXTENSION_TO_FONT_MIME: Record<string, string> = {
  '.woff2': 'font/woff2',
  '.woff': 'font/woff',
  '.ttf': 'font/ttf',
  '.otf': 'font/otf',
  '.eot': 'application/vnd.ms-fontobject',
};

const MAX_FILE_SIZE_MB = {
  audio: AUDIO_MAX_FILE_SIZE_MB,
  document: DOCUMENT_MAX_FILE_SIZE_MB,
  font: FONT_MAX_FILE_SIZE_MB,
  image: IMAGE_MAX_FILE_SIZE_MB,
  video: VIDEO_MAX_FILE_SIZE_MB,
};

type MediaClass = keyof typeof MAX_FILE_SIZE_MB;

export const normalizeMimeType = (filename: string, mimeType: string): string => {
  const base = mimeType.split(';')[0].trim();
  if (base !== 'application/octet-stream') return base;

  const ext = filename.slice(filename.lastIndexOf('.')).toLowerCase();
  return EXTENSION_TO_FONT_MIME[ext] ?? base;
};

/** Fonts are split across the `font/*` and `application/*` trees, so the table maps them to one class. */
export const mediaClassOf = (mimeType: string): string => MEDIA_TYPES[mimeType]?.mediaClass ?? mimeType.split('/')[0];

export const isAllowedMimeType = (mimeType: string): boolean => mimeType in MEDIA_TYPES;

/**
 * The Cloudinary resource type: it picks the upload endpoint and the delete call. Fonts are `raw`.
 * Cloudinary stores audio under `video`, and a PDF under `image` so its pages can be rendered. A
 * delete must ask for the type Cloudinary used, or it targets a `raw` asset that never existed and
 * leaves the file behind.
 */
const CLOUDINARY_RESOURCE_TYPES: Record<string, string> = { audio: 'video', document: 'image', image: 'image', video: 'video' };

export const cloudinaryResourceTypeFor = (mimeType: string): string => CLOUDINARY_RESOURCE_TYPES[mediaClassOf(mimeType)] ?? 'raw';

/** Cloudinary rejects a format outside this list on the real bytes, before storing anything. */
export const allowedFormatsFor = (mediaClass: string): null | string => {
  const formats = Object.values(MEDIA_TYPES)
    .filter((type) => type.mediaClass === mediaClass)
    .flatMap((type) => type.formats);

  return formats.length ? [...new Set(formats)].join(',') : null;
};

export const unsupportedFormatMessage = (filename: string, mimeType: string): string =>
  `"${filename}" has an unsupported format (${mimeType}). Only standard image, video, audio, font, and PDF formats are allowed.`;

export const sizeLimitMessage = (filename: string, filesize: number, limitMb: number): string =>
  `"${filename}" exceeds the ${limitMb} MB file size limit (${bytesToMB(filesize)} MB).`;

export const dimensionLimitMessage = (filename: string, width: number, height: number): string =>
  `"${filename}" dimensions (${width}x${height}) exceed the ${IMAGE_MAX_DIMENSION_PX}x${IMAGE_MAX_DIMENSION_PX} px limit.`;

/** Returns the reason the upload is refused, or `null` when the declared metadata is acceptable. */
export const declaredUploadViolation = (filename: string, filesize: number, mimeType: string): null | string => {
  const resolved = normalizeMimeType(filename, mimeType);
  const mediaType = MEDIA_TYPES[resolved];

  if (!mediaType) return unsupportedFormatMessage(filename, resolved);

  const limitMb = resolved === SVG_MIME_TYPE ? SVG_MAX_FILE_SIZE_MB : MAX_FILE_SIZE_MB[mediaType.mediaClass];

  return filesize > limitMb * BYTES_PER_MB ? sizeLimitMessage(filename, filesize, limitMb) : null;
};

export const dimensionViolation = (filename: string, width?: null | number, height?: null | number): null | string =>
  (width && width > IMAGE_MAX_DIMENSION_PX) || (height && height > IMAGE_MAX_DIMENSION_PX)
    ? dimensionLimitMessage(filename, width ?? 0, height ?? 0)
    : null;
